/**
 * WorkflowConditionControls - Controles do construtor de filtros do Workflow 2.0
 *
 * Reúne os controles usados por cada linha de regra:
 *   - `SeletorDeCampo`  : busca com agrupamento por módulo (Negócio, Produtos,
 *                         Cliente, Propostas, Financeiro, Contexto);
 *   - `SeletorDeValores`: lista suspensa (única ou múltipla) para campos que
 *                         possuem catálogo — elimina a digitação livre de UUID;
 *   - `ValorDaRegra`    : escolhe o controle certo conforme tipo/operador.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-01 v1.0.0 - Criação inicial (filtros robustos do gatilho)
 */

import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

import {
  camposDaEntidade,
  ordemDosGrupos,
  acharCampo,
  converterValorDoCampo,
  metaDoOperador,
  rotuloDoCampo,
  type CampoDoCatalogo,
} from './camposDoNegocio';
import type { OpcaoDeFiltro } from '@/hooks/useWorkflowFilterSources';

// =============================================================================
// SELETOR DE CAMPO (busca + agrupamento por módulo)
// =============================================================================

export function SeletorDeCampo({
  valor,
  onChange,
  className,
  entidade,
}: {
  valor: string;
  onChange: (campo: CampoDoCatalogo) => void;
  className?: string;
  /** Entidade-raiz do processo (crm_deal | billing). Padrão: Negócio */
  entidade?: string | null;
}) {
  const [aberto, setAberto] = useState(false);
  const moduloOs = useServiceOrderModuleEnabled();

  /** Campos agrupados na ordem definida pelo catálogo da entidade-raiz */
  const grupos = useMemo(() => {
    const ordem = ordemDosGrupos(entidade);
    const mapa = new Map<string, CampoDoCatalogo[]>();
    camposDaEntidade(entidade).forEach((c) => {
      // v1.4.0: grupo de O.S. só com o módulo Ordem de Serviço ligado
      if (c.grupo === 'Ordens de serviço' && !moduloOs) return;
      const atual = mapa.get(c.grupo) ?? [];
      atual.push(c);
      mapa.set(c.grupo, atual);
    });
    return Array.from(mapa.entries()).sort((a, b) => ordem.indexOf(a[0]) - ordem.indexOf(b[0]));
  }, [entidade, moduloOs]);


  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn('h-8 justify-between gap-1 rounded-lg px-2 text-xs font-normal', className)}
        >
          <span className="truncate">{valor ? rotuloDoCampo(valor) : 'Escolher campo'}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar campo..." className="h-9 text-xs" />
          <CommandList className="max-h-[300px]">
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
              Nenhum campo encontrado.
            </CommandEmpty>
            {grupos.map(([grupo, campos]) => (
              <CommandGroup key={grupo} heading={grupo}>
                {campos.map((campo) => (
                  <CommandItem
                    key={campo.campo}
                    value={`${grupo} ${campo.rotulo} ${campo.campo}`}
                    onSelect={() => {
                      onChange(campo);
                      setAberto(false);
                    }}
                    className="text-xs"
                  >
                    <Check
                      className={cn('mr-2 h-3.5 w-3.5', valor === campo.campo ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="truncate">{campo.rotulo}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// SELETOR DE VALORES (lista suspensa única ou múltipla)
// =============================================================================

export function SeletorDeValores({
  opcoes,
  selecionados,
  multiplo,
  onChange,
}: {
  opcoes: OpcaoDeFiltro[];
  selecionados: string[];
  multiplo: boolean;
  onChange: (valores: string[]) => void;
}) {
  const [aberto, setAberto] = useState(false);

  const alternar = (valor: string) => {
    if (!multiplo) {
      onChange([valor]);
      setAberto(false);
      return;
    }
    onChange(
      selecionados.includes(valor) ? selecionados.filter((v) => v !== valor) : [...selecionados, valor],
    );
  };

  const rotulo = (valor: string) => opcoes.find((o) => o.value === valor)?.label ?? valor;

  return (
    <div className="flex min-w-[160px] flex-1 flex-col gap-1">
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="h-8 w-full justify-between gap-1 rounded-lg px-2 text-xs font-normal"
          >
            <span className="truncate">
              {selecionados.length === 0
                ? 'Selecionar...'
                : selecionados.length === 1
                  ? rotulo(selecionados[0])
                  : `${selecionados.length} selecionados`}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar..." className="h-9 text-xs" />
            <CommandList className="max-h-[260px]">
              <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
                Nada encontrado.
              </CommandEmpty>
              <CommandGroup>
                {opcoes.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={`${o.label} ${o.grupo ?? ''}`}
                    onSelect={() => alternar(o.value)}
                    className="text-xs"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-3.5 w-3.5',
                        selecionados.includes(o.value) ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="truncate">{o.label}</span>
                    {o.grupo && (
                      <span className="ml-auto truncate pl-2 text-[10px] text-muted-foreground">{o.grupo}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Fichas dos valores escolhidos (só na seleção múltipla) */}
      {multiplo && selecionados.length > 1 && (
        <div className="flex flex-wrap gap-1">
          {selecionados.map((v) => (
            <Badge key={v} variant="secondary" className="gap-1 px-1.5 py-0 text-[10px] font-normal">
              {rotulo(v)}
              <button
                type="button"
                aria-label={`Remover ${rotulo(v)}`}
                onClick={() => onChange(selecionados.filter((x) => x !== v))}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// VALOR DA REGRA (escolhe o controle certo por tipo + operador)
// =============================================================================

export function ValorDaRegra({
  campo,
  operador,
  value,
  value2,
  opcoes,
  onChange,
}: {
  campo: string;
  operador: string;
  value: unknown;
  value2: unknown;
  opcoes: OpcaoDeFiltro[];
  onChange: (patch: { value?: unknown; value2?: unknown }) => void;
}) {
  const meta = metaDoOperador(campo, operador);
  const definicao = acharCampo(campo);

  // Operadores unários (vazio / preenchido / sim / não) não pedem valor
  if (meta?.semValor) return null;

  // Campos com catálogo (uuid ou enum) usam lista suspensa
  const temCatalogo = !!definicao?.fonte || !!definicao?.opcoes;
  if (temCatalogo) {
    const selecionados = Array.isArray(value) ? (value as string[]) : value ? [String(value)] : [];
    return (
      <SeletorDeValores
        opcoes={opcoes}
        selecionados={selecionados}
        multiplo={!!meta?.multiplo}
        onChange={(valores) => onChange({ value: meta?.multiplo ? valores : (valores[0] ?? '') })}
      />
    );
  }

  // Intervalo numérico (entre X e Y)
  if (meta?.intervalo) {
    return (
      <div className="flex flex-1 items-center gap-1">
        <Input
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange({ value: converterValorDoCampo(campo, e.target.value.slice(0, 60)) })}
          placeholder="De"
          className="h-8 w-[90px] rounded-lg text-xs"
        />
        <span className="text-[11px] text-muted-foreground">e</span>
        <Input
          value={value2 === undefined || value2 === null ? '' : String(value2)}
          onChange={(e) => onChange({ value2: converterValorDoCampo(campo, e.target.value.slice(0, 60)) })}
          placeholder="Até"
          className="h-8 w-[90px] rounded-lg text-xs"
        />
      </div>
    );
  }

  // Data
  if (definicao?.tipo === 'data') {
    return (
      <Input
        type="date"
        value={value ? String(value) : ''}
        onChange={(e) => onChange({ value: e.target.value })}
        className="h-8 w-[150px] rounded-lg text-xs"
      />
    );
  }

  // Texto / número livre (limitado para evitar payload abusivo)
  return (
    <Input
      value={value === undefined || value === null ? '' : String(value)}
      onChange={(e) => onChange({ value: converterValorDoCampo(campo, e.target.value.slice(0, 200)) })}
      placeholder="Valor"
      className="h-8 min-w-[120px] flex-1 rounded-lg text-xs"
      maxLength={200}
    />
  );
}
